import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { MdSearch } from 'react-icons/md'
import Navbar from './Navbar'
import TableList from './TableList'

export default function LoanApplications() {
  const { loanApplications = [] } = useSelector(state => state)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  const filteredApplications = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()

    return loanApplications.filter(application => {
      const matchesQuery = !query || [
        application.email,
        application.property_address,
        application.landlord_account_name
      ].some(value => String(value || '').toLowerCase().includes(query))

      const matchesStatus =
        statusFilter === 'all' || application.status === statusFilter

      return matchesQuery && matchesStatus
    })
  }, [loanApplications, searchQuery, statusFilter])

  const statuses = Array.from(
    new Set(loanApplications.map(item => item.status).filter(Boolean))
  )

  return (
    <div className="flex min-h-screen w-full bg-[#F7F9FC] font-[Satoshi]">
      <Navbar />

      <main className="min-w-0 flex-1 px-5 pb-10 pt-24 sm:px-6 lg:px-8 lg:pt-8">
        <div className="mx-auto max-w-[1500px]">
          <div className="mb-7">
            <p className="text-sm font-semibold text-green-600">APPLICATIONS</p>
            <div className="mt-1 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-gray-900">Loan applications</h1>
                <p className="mt-2 text-sm text-gray-500">
                  Review every rent-financing application and its landlord payment details.
                </p>
              </div>

              <div className="rounded-full border border-gray-200 bg-white px-3.5 py-2 text-xs font-semibold text-gray-600 shadow-sm">
                {loanApplications.length} total application{loanApplications.length === 1 ? '' : 's'}
              </div>
            </div>
          </div>

          <div className="mb-5 grid gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm md:grid-cols-[minmax(0,1fr)_220px]">
            <div className="relative">
              <MdSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-xl text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={event => setSearchQuery(event.target.value)}
                placeholder="Search by email, property or landlord..."
                className="h-12 w-full rounded-xl border border-gray-300 bg-white pl-11 pr-4 text-sm text-gray-900 outline-none transition focus:border-green-500 focus:ring-4 focus:ring-green-100"
              />
            </div>

            <select
              value={statusFilter}
              onChange={event => setStatusFilter(event.target.value)}
              className="h-12 rounded-xl border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 outline-none transition focus:border-green-500 focus:ring-4 focus:ring-green-100"
            >
              <option value="all">All statuses</option>
              {statuses.map(status => (
                <option key={status} value={status}>
                  {status.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </div>

          {filteredApplications.length > 0 ? (
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-[1600px] w-full text-left text-sm">
                  <thead className="bg-gray-50 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                    <tr>
                      <th className="px-5 py-4">Customer</th>
                      <th className="px-5 py-4">Status</th>
                      <th className="px-5 py-4">Rent</th>
                      <th className="px-5 py-4">Address</th>
                      <th className="px-5 py-4">Property</th>
                      <th className="px-5 py-4">Landlord phone</th>
                      <th className="px-5 py-4">Bank</th>
                      <th className="px-5 py-4">Account number</th>
                      <th className="px-5 py-4">Account name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredApplications.map((application, index) => (
                      <TableList
                        key={application.id || application.email || index}
                        user={application}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center">
              <p className="text-lg font-semibold text-gray-700">No applications found</p>
              <p className="mt-2 text-sm text-gray-400">Try a different search or status filter.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
